import { useState } from "react";
import { useInfiniteQuery, useQueryClient } from "@tanstack/react-query";
import { useAtomValue } from "jotai";

import CommentsAPI from "../../api/CommentsAPI";
import { Comment } from "./useCreateComment";
import { tenantIDAtom } from "../../../atoms/GlobalAtoms";

export type ConversationUser = {
  _id: string;
  _RecordName: string;
  _ModuleAPIName: string;
  ProfileImage: any;
};

export type ConversationUsersMap = {
  [userID: string]: ConversationUser;
};

export interface CommentsQuery {
  comments: Comment[];
  hasNextPage: boolean;
  totalComments: number;
  pageNumber: number;
  users?: ConversationUsersMap;
  isASubscriber: boolean;
}

// Designed for Infinite scrolling.
export default function useFetchComments(
  context: string,
  contextID: string | undefined,
  disabled: boolean,
  readNotifactions = true
) {
  // Custom loadingNext because .isFetchingNextPage wasn't working for some reason.
  const [loadingNext, setLoadingNext] = useState(false);
  const queryClient = useQueryClient();
  const tenantID = useAtomValue(tenantIDAtom);

  const comments = useInfiniteQuery<CommentsQuery>({
    initialPageParam: 1,
    queryKey: ["comments", context, contextID],
    queryFn: ({ pageParam = 1 }) => {
      (pageParam as number) > 1 && setLoadingNext(true);

      return CommentsAPI.fetchComments(
        context,
        contextID!,
        pageParam as number,
        readNotifactions
      )
        .then((data) => {
          const { notifications, ...commentsInfo } = data.data.data;

          if (notifications) {
            queryClient.setQueryData(["user", tenantID], (prev: any) => {
              return {
                ...(prev || {}),
                user: { ...prev.user, Notifications: notifications },
              };
            });
          }

          return commentsInfo;
        })
        .finally(() => setLoadingNext(false));
    },
    getNextPageParam: (lastPage) =>
      lastPage?.hasNextPage ? lastPage?.pageNumber + 1 : undefined,
    enabled: !disabled && !!contextID,
  });

  return {
    commentPages: comments.data?.pages || [],
    loading: comments.isFetching,
    loadingNext: !disabled && loadingNext,
    hasMore: comments.hasNextPage,
    fetchNextPage: comments.fetchNextPage,
  };
}
